<template>
  <div class="container">
    <donut-page-title title="Roles" />
    <template v-if="props.hasTags">
      <donut-tabs class="m-tx4" :tabs="tabs" />
      <donut-table
        class="m-tx4"
        :headers="tableHeaders"
        :data="tableData"
        :content-centered="false"
      >
        <template #role="{ entry }">
          <button v-if="entry.isNewRoleRow" @click="openModal(null, 'create')">New Role</button>
          <div v-else class="t">{{ entry.name }}</div>
        </template>
        <template #journeys="{ entry }">
          <div v-if="!entry.isNewRoleRow" class="tags">
            <div v-for="tag in entry.tags" :key="tag.id" class="tag">{{ tag.name }}</div>
          </div>
        </template>
        <template #messages="{ entry }">
          <div v-if="!entry.isNewRoleRow" class="t">{{ entry.schedule_events.length }} messages</div>
        </template>
        <template #actions="{ entry }">
          <div v-if="!entry.isNewRoleRow && entry.purpose !== 'primary'" class="f">
            <button class="inv ic" @click="openModal(entry, 'edit')"><i class="mi">edit</i></button>
            <template v-if="!entry.purpose">
              <button v-if="entry.active" class="inv ic m-l" @click="openModal(entry, 'archive')"><i class="mi">archive</i></button>
              <button v-else class="inv ic m-l" @click="openModal(entry, 'unarchive')"><i class="mi">unarchive</i></button>
            </template>
          </div>
        </template>
      </donut-table>
    </template>
    <template v-else>
      <div class="separator m-vx2" />
      <donut-empty
        title="First things first — you need a Journey!"
        image="empty/roles"
        action-u-r-l="/onboarding/tags/overview"
        action-label="Create Journey"
      >
        <template #description>
          Roles will let you manage who's involved in your <strong>Journeys</strong>. Think <strong>New Hire</strong>, <strong>Manager</strong>, <strong>Buddy</strong>, etc. The sky's the limit!
        </template>
      </donut-empty>
    </template>
  </div>
</template>

<script setup>
  import { ref, reactive, computed } from 'vue';
  import { useStore } from 'vuex';

  const props = defineProps({
    hasTags: { type: Boolean, required: true },
    roles: { type: Array, default: () => [] },
  });

  const { commit } = useStore();

  const allRoles = reactive(props.roles);
  const activeRoles = computed(() => allRoles.filter(role => role.active));
  const hiddenRoles = computed(() => allRoles.filter(role => !role.active));

  // Tabs
  const selectedTab = ref('active');
  const selectActive = () => { selectedTab.value = 'active'; };
  const selectArchived = () => { selectedTab.value = 'archived'; };
  const tabs = computed(() => [
      { title: `Active (${activeRoles.value.length})`, value: 'active', onClick: selectActive },
      { title: `Archived (${hiddenRoles.value.length})`, value: 'archived', onClick: selectArchived },
    ]);

  // Table
  const tableHeaders = [
    { label: 'Role', key: 'role', weight: 1 },
    { label: 'Journeys', key: 'journeys', weight: 2 },
    { label: 'Messages', key: 'messages', weight: 1 },
    { label: '', key: 'actions', weight: 1 },
  ];
  const tableData = computed(() => {
    if (selectedTab.value === 'active') {
      return activeRoles.value.concat([{ isNewRoleRow: true }]);
    }
      return hiddenRoles.value;
  });

  // Editing
  const onCreate = ({ id, name }) => {
    allRoles.push({ id, name, active: true, purpose: null, schedule_events: [], tags: [] });
  };
  const onEdit = ({ id, name }) => { allRoles.find(role => role.id === id).name = name; };
  const onArchive = ({ id, name }) => {
    const role = allRoles.find(r => r.id === id);
    role.active = false;
    role.name = name; // Name changes when archiving, adds an archival suffix
  };
  const onUnarchive = ({ id }) => { allRoles.find(role => role.id === id).active = true; };
  const openModal = (role, editType) => {
    commit('updateModal', {
      visible: true,
      subcomponent: 'onboarding-roles-modals-edit',
      data: { role, editType, onCreate, onEdit, onArchive, onUnarchive },
    });
  };
</script>
